import React from "react";
import { NotebookPen, Link2, Quote, HelpCircle, Trash2, Send, CheckCircle2, ExternalLink, Boxes } from "lucide-react";
import { Button } from "@/components/ui/button";
import { safeUrl } from "@/lib/safeUrl";
import { useAuth } from "@/context/AuthContext";

const META = {
  note: { Icon: NotebookPen, label: "Note", tone: "text-foreground" },
  link: { Icon: Link2, label: "Link", tone: "text-primary" },
  quote: { Icon: Quote, label: "Quote", tone: "text-accent" },
  question: { Icon: HelpCircle, label: "Question", tone: "text-amber-600" },
};

const domainOf = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };

export const EntryCard = ({ entry, onSuggest, onDelete, onExtract }) => {
  const { user } = useAuth();
  const isCurator = user && (user.role === "curator" || user.role === "admin");
  const meta = META[entry.entry_type] || META.note;
  const { Icon } = meta;
  const href = safeUrl(entry.url);
  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid={`entry-${entry.id}`}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${meta.tone}`}><Icon className="w-4 h-4" /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{meta.label}</span>
            {entry.node_title && (
              <span className="text-[10px] text-muted-foreground/80 truncate">· {entry.node_title}</span>
            )}
          </div>
          {entry.entry_type === "quote"
            ? <blockquote className="border-l-2 border-accent/50 pl-3 mt-1 text-sm italic text-foreground whitespace-pre-line">{entry.text}</blockquote>
            : entry.text && <p className="text-sm text-foreground mt-1 whitespace-pre-line">{entry.text}</p>}
          {href && (
            <a href={href} target="_blank" rel="noreferrer noopener" data-testid={`entry-link-${entry.id}`}
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary hover:underline max-w-full">
              <ExternalLink className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{entry.label || domainOf(href)}</span>
            </a>
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-border/60">
        {entry.suggested ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-accent px-2" data-testid={`entry-suggested-${entry.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Suggested to curators
          </span>
        ) : (
          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-primary"
            onClick={() => onSuggest(entry)} data-testid={`suggest-entry-${entry.id}`}>
            <Send className="w-3.5 h-3.5 mr-1" /> Suggest to curators
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDelete(entry.id)} data-testid={`delete-entry-${entry.id}`}>
          <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
        </Button>
      </div>
      {isCurator && onExtract && (entry.text || "").trim() && (
        <div className="flex justify-end mt-1">
          <Button variant="ghost" size="sm" className="h-7 text-xs text-primary"
            onClick={() => onExtract(entry)} data-testid={`extract-entry-${entry.id}`}>
            <Boxes className="w-3.5 h-3.5 mr-1" /> Extract entities
          </Button>
        </div>
      )}
    </div>
  );
};
